import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Clock, Home, Edit, Trash2, Download, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api, { hrAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function TimeTracking() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('me');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [sendingReport, setSendingReport] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: '', clock_out: '', clock_in_notes: '', clock_out_notes: '' });
  const [savingEntry, setSavingEntry] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Convert ISO → datetime-local form value (YYYY-MM-DDTHH:mm) in local tz.
  const isoToLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const localInputToIso = (val) => (val ? new Date(val).toISOString() : null);

  const openEdit = (entry) => {
    setEditEntry(entry);
    setEditForm({
      clock_in: isoToLocalInput(entry.clock_in),
      clock_out: isoToLocalInput(entry.clock_out),
      clock_in_notes: entry.clock_in_notes || '',
      clock_out_notes: entry.clock_out_notes || '',
    });
  };

  const saveEdit = async () => {
    if (!editEntry) return;
    setSavingEntry(true);
    try {
      const payload = {
        clock_in: localInputToIso(editForm.clock_in),
        clock_out: editForm.clock_out ? localInputToIso(editForm.clock_out) : null,
        clock_in_notes: editForm.clock_in_notes,
        clock_out_notes: editForm.clock_out_notes,
      };
      await api.patch(`/time/entries/${editEntry.id}`, payload);
      toast.success('Shift updated');
      setEditEntry(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update shift');
    } finally {
      setSavingEntry(false);
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Delete ${entry.user_name}'s shift on ${entry.date}? This cannot be undone.`)) return;
    setDeletingId(entry.id);
    try {
      await api.delete(`/time/entries/${entry.id}`);
      toast.success('Shift deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete shift');
    } finally {
      setDeletingId(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { days };
      if (isAdmin && selectedUserId && selectedUserId !== 'me') params.user_id = selectedUserId;
      const res = await api.get('/time/entries.csv', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `time-entries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error('CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  const canEdit = (entry) => isAdmin || entry.user_id === user?.id;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      const summaryParams = { days };
      if (isAdmin && selectedUserId && selectedUserId !== 'me') {
        params.user_id = selectedUserId;
        summaryParams.user_id = selectedUserId;
      }
      const [entriesRes, summaryRes] = await Promise.all([
        api.get('/time/entries', { params }),
        api.get('/time/summary', { params: summaryParams }),
      ]);
      setEntries(entriesRes.data || []);
      setSummary(summaryRes.data || null);
    } catch (err) {
      console.error('Failed to load time entries:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedUserId, days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!isAdmin) return;
    // Load agents list for admin filter
    (async () => {
      try {
        const res = await api.get('/users', { params: { role: 'agent' } });
        const adminsRes = await api.get('/users', { params: { role: 'admin' } });
        setAgents([...(res.data || []), ...(adminsRes.data || [])]);
      } catch (err) {
        console.error('Failed to load agents:', err);
      }
    })();
  }, [isAdmin]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString();
  };

  const formatDuration = (minutes) => {
    if (minutes == null) return '—';
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h}h ${m}m`;
  };

  // Per-agent totals (admin) — computed from the same loaded entries shown
  // in the Shift History table below.
  const agentTotals = Object.values(
    entries.reduce((acc, e) => {
      const key = e.user_id;
      if (!acc[key]) acc[key] = { user_id: key, name: e.user_name, role: e.user_role, minutes: 0, shifts: 0 };
      acc[key].minutes += e.duration_minutes || 0;
      acc[key].shifts += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.minutes - a.minutes);
  const maxMinutes = agentTotals[0]?.minutes || 1;

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="text-slate-600 hover:text-cyan-700"
          data-testid="back-to-dashboard-btn"
        >
          <Home className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 mb-1">Time Tracking</h1>
          <p className="text-sm text-slate-500">
            {isAdmin ? "View and manage timesheets for all agents." : "Review your shift history."} <span className="text-slate-400">All times MST.</span>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isAdmin && (
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="w-56 bg-white border-slate-200" data-testid="tt-user-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="me">My timesheet</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} ({a.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="w-40 bg-white border-slate-200" data-testid="tt-range-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Total Hours</p>
            <p className="text-2xl font-bold text-cyan-600" data-testid="tt-total-hours">
              {summary ? summary.total_hours.toFixed(2) : '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Shifts Logged</p>
            <p className="text-2xl font-bold text-slate-800" data-testid="tt-total-entries">
              {summary ? summary.total_entries : '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Days Worked</p>
            <p className="text-2xl font-bold text-slate-800">
              {summary ? Object.keys(summary.by_date || {}).length : '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Avg / Shift</p>
            <p className="text-2xl font-bold text-slate-800">
              {summary && summary.total_entries > 0
                ? `${(summary.total_hours / summary.total_entries).toFixed(2)}h`
                : '—'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-agent hour totals (admin only) */}
      {isAdmin && !loading && agentTotals.length > 0 && (
        <Card className="bg-white border-slate-200 mb-6" data-testid="tt-agent-totals">
          <CardHeader className="border-b border-slate-200 py-4 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-slate-800 text-base md:text-lg flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-500" />
              Hours by Agent
              <span className="text-xs font-normal text-slate-400 ml-1">across shifts below</span>
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              disabled={sendingReport}
              onClick={async () => {
                setSendingReport(true);
                try {
                  const res = await hrAPI.sendWeeklyReport();
                  const d = res.data;
                  if (d.sent > 0) toast.success(`Weekly report emailed to ${d.sent} admin${d.sent === 1 ? '' : 's'} (${d.agents} agents, ${d.period})`);
                  else if (d.skipped > 0) toast.info('Email not configured — report skipped (see Email Settings)');
                  else if (d.admins === 0) toast.info('No admin accounts with an email address found');
                  else toast.error('Report email failed — check Email Settings → Recent Email Activity');
                } catch (err) {
                  toast.error('Failed to send weekly report');
                } finally {
                  setSendingReport(false);
                }
              }}
              data-testid="tt-send-weekly-report-btn"
            >
              {sendingReport ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Email Weekly Report
            </Button>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {agentTotals.map((a) => (
              <div key={a.user_id} className="flex items-center gap-3" data-testid={`tt-agent-total-${a.user_id}`}>
                <div className="w-44 md:w-56 shrink-0 flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-slate-800 truncate">{a.name}</span>
                  <Badge variant="outline" className="text-[10px] capitalize shrink-0">{a.role}</Badge>
                </div>
                <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-cyan-500 rounded-full transition-[width] duration-500"
                    style={{ width: `${Math.max((a.minutes / maxMinutes) * 100, 2)}%` }}
                  />
                </div>
                <div className="w-28 shrink-0 text-right">
                  <span className="font-mono text-sm text-slate-800">{formatDuration(a.minutes)}</span>
                  <span className="text-xs text-slate-400 ml-1">· {a.shifts} shift{a.shifts === 1 ? '' : 's'}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Entries table */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="border-b border-slate-200">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-slate-800 text-base md:text-lg flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-500" />
              Shift History
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-slate-300"
              onClick={exportCsv}
              disabled={exporting || entries.length === 0}
              data-testid="tt-export-csv-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
            </div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-slate-500" data-testid="tt-empty">
              No shifts logged yet. Click <strong>Clock In</strong> in the sidebar to start.
            </div>
          ) : (
            <Table data-testid="tt-entries-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id} data-testid={`tt-entry-${e.id}`}>
                    <TableCell className="font-medium text-slate-800">
                      {e.user_name}
                      <Badge variant="outline" className="ml-2 text-[10px] capitalize">
                        {e.user_role}
                      </Badge>
                    </TableCell>
                    <TableCell>{e.date}</TableCell>
                    <TableCell className="text-slate-600">{formatDate(e.clock_in)}</TableCell>
                    <TableCell className="text-slate-600">
                      {e.clock_out ? formatDate(e.clock_out) : (
                        <Badge className="bg-cyan-100 text-cyan-700">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        {formatDuration(e.duration_minutes)}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[220px] truncate">
                      {[e.clock_in_notes, e.clock_out_notes].filter(Boolean).join(' / ') || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canEdit(e) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEdit(e)}
                            title="Edit shift"
                            data-testid={`tt-edit-${e.id}`}
                          >
                            <Edit className="w-4 h-4 text-cyan-600" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => deleteEntry(e)}
                            disabled={deletingId === e.id}
                            title="Delete shift"
                            data-testid={`tt-delete-${e.id}`}
                          >
                            {deletingId === e.id
                              ? <Loader2 className="w-4 h-4 animate-spin text-rose-600" />
                              : <Trash2 className="w-4 h-4 text-rose-600" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Shift dialog */}
      <Dialog open={!!editEntry} onOpenChange={(o) => { if (!o) setEditEntry(null); }}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] max-h-[90dvh] overflow-y-auto" data-testid="tt-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Clock In</label>
                <Input
                  type="datetime-local"
                  value={editForm.clock_in}
                  onChange={(e) => setEditForm((f) => ({ ...f, clock_in: e.target.value }))}
                  data-testid="tt-edit-clock-in"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Clock Out (leave empty for active shift)</label>
                <Input
                  type="datetime-local"
                  value={editForm.clock_out}
                  onChange={(e) => setEditForm((f) => ({ ...f, clock_out: e.target.value }))}
                  data-testid="tt-edit-clock-out"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Clock-in notes</label>
              <Textarea
                rows={2}
                value={editForm.clock_in_notes}
                onChange={(e) => setEditForm((f) => ({ ...f, clock_in_notes: e.target.value }))}
                data-testid="tt-edit-in-notes"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Clock-out notes</label>
              <Textarea
                rows={2}
                value={editForm.clock_out_notes}
                onChange={(e) => setEditForm((f) => ({ ...f, clock_out_notes: e.target.value }))}
                data-testid="tt-edit-out-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditEntry(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEntry} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" data-testid="tt-edit-save-btn">
              {savingEntry ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
